// Personal "You" hub data layer.
//
// Backs the chat-focused activity hub — the team member's Slack-style "me" tab:
// @-mentions, DMs, thread replies, and tickets assigned to them, merged
// reverse-chronologically. Reads the keyset-paginated feed from
//   POST /api/v1/event/:eventId/me/activity-feed
// (see quickback/features/chat/actions/getMyActivity.ts).
//
// This is DELIBERATELY separate from core/activity.ts — that one is the org-wide
// audit feed ("what happened in the event"); this is "what needs MY attention".

import { infiniteQueryOptions } from "@tanstack/react-query"
import { api } from "./api"

export type MyActivityKind = "mention" | "dm" | "thread" | "ticket"

// Tab filters — "assigned" maps to the ticket source (+ tasks once organizer
// tasks land server-side). "all" merges every source.
export type MyActivityFilter =
  | "all"
  | "mentions"
  | "dms"
  | "threads"
  | "assigned"

// Mirrors the backend ActivityItem. `kind`/`surface`/`status` are plain strings
// (not the narrow unions) for the same forward-compat reason core/activity.ts
// keeps them loose: a server-side enum addition never trips a strict decoder.
export type MyActivityItem = {
  id: string
  kind: string
  occurredAt: string
  actorUserId: string | null
  actorName: string | null
  surface: "channel" | "conversation" | "ticket" | string
  conversationId: string | null
  parentMessageId: string | null
  ticketId: string | null
  contextLabel: string
  excerpt: string
  unread: boolean
  status: string | null
  priority: string | null
}

export type MyActivityCounts = { assigned: number }

export type MyActivityPage = {
  success: true
  items: MyActivityItem[]
  nextCursor: string | null
  hasMore: boolean
  counts: MyActivityCounts
}

export const MY_ACTIVITY_PAGE_SIZE = 30

// Display order of the tabs. Keep in sync with the route's tab bar.
export const MY_ACTIVITY_FILTERS: MyActivityFilter[] = [
  "all",
  "mentions",
  "dms",
  "threads",
  "assigned",
]

export const MY_ACTIVITY_FILTER_LABEL: Record<MyActivityFilter, string> = {
  all: "All",
  mentions: "Mentions",
  dms: "DMs",
  threads: "Threads",
  assigned: "Assigned",
}

// Keyset-paginated infinite feed. `enabled` is gated on eventId by the caller.
// Keyed by (eventId, filter, query) so switching tabs or typing a search is a
// fresh query — each combination runs a different source set server-side.
export function myActivityFeedQueryOptions(
  eventId: string | undefined,
  filter: MyActivityFilter,
  query = "",
) {
  const q = query.trim()
  return infiniteQueryOptions({
    queryKey: ["my-activity", eventId, filter, q] as const,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api<MyActivityPage>(`/api/v1/event/${eventId}/me/activity-feed`, {
        method: "POST",
        body: {
          filter,
          query: q || undefined,
          cursor: pageParam ?? undefined,
          limit: MY_ACTIVITY_PAGE_SIZE,
        },
      }),
    getNextPageParam: (last: MyActivityPage) => last.nextCursor ?? undefined,
  })
}
